/**
 * Generador de URLs amigables para el catálogo
 * Estructura: /catalogo/[categoria]/[subcategoria]?filtros
 */

/**
 * Convierte texto con espacios a formato slug (guiones)
 * @param {string} text - Texto a convertir
 * @returns {string} - Texto convertido a slug
 */
const textToSlug = (text) => {
    if (!text) return '';
    return text
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')           // Espacios a guiones
        .replace(/[^\w-]/g, '')        // Remover caracteres especiales
        .replace(/-+/g, '-')           // Múltiples guiones a uno solo
        .replace(/^-+|-+$/g, '');      // Remover guiones al inicio y final
};

/**
 * Encuentra el nombre de una opción por su ID en los datos de filtros
 * @param {Array} data - Array de opciones
 * @param {number|string} id - ID a buscar
 * @returns {string} - Nombre encontrado o string vacío
 */
const findNameById = (data, id) => {
    if (!data || !Array.isArray(data) || !id) return '';
    const item = data.find(item => item.id === id);
    return item ? item.nombre || item.name || '' : '';
};

/**
 * Genera URL amigable basada en filtros seleccionados
 * @param {Object} filters - Filtros actuales
 * @param {Object} filterData - Datos de filtros (categorías, subcategorías, etc.)
 * @returns {string} - URL amigable generada
 */
export const generateFriendlyUrl = (filters, filterData, searchTerm = '') => {
    console.log('🔗 generateFriendlyUrl - Generando URL con filtros:', filters);
    console.log('🔗 generateFriendlyUrl - Datos de filtros:', filterData);
    console.log('🔗 generateFriendlyUrl - Término de búsqueda:', searchTerm);
    
    let path = '/catalogo';
    const queryParams = new URLSearchParams();
    
    // Construir jerarquía principal: /catalogo/[categoria]/[subcategoria]
    if (filters.categoria && filterData.categorias) {
        const categoriaNombre = findNameById(filterData.categorias, filters.categoria);
        if (categoriaNombre) {
            path += `/${textToSlug(categoriaNombre)}`;
            
            // Agregar subcategoría si existe
            if (filters.subcategoria && filterData.subcategorias) {
                const subcategoriaNombre = findNameById(filterData.subcategorias, filters.subcategoria);
                if (subcategoriaNombre) {
                    path += `/${textToSlug(subcategoriaNombre)}`;
                }
            }
        }
    }
    
    // Agregar filtros secundarios como parámetros de consulta
    if (filters.marca && filterData.marcas) {
        const marcaNombre = findNameById(filterData.marcas, filters.marca);
        if (marcaNombre) {
            queryParams.set('marca', textToSlug(marcaNombre));
        }
    }
    
    if (filters.canal && filterData.canales) {
        const canalNombre = findNameById(filterData.canales, filters.canal);
        if (canalNombre) {
            queryParams.set('canal', textToSlug(canalNombre));
        }
    }
    
    if (filters.linea && filterData.lineas) {
        const lineaNombre = findNameById(filterData.lineas, filters.linea);
        if (lineaNombre) {
            queryParams.set('linea', textToSlug(lineaNombre));
        }
    }
    
    if (filters.concepto && filterData.conceptos) {
        const conceptoNombre = findNameById(filterData.conceptos, filters.concepto);
        if (conceptoNombre) {
            queryParams.set('concepto', textToSlug(conceptoNombre));
        }
    }
    
    // Agregar filtros de precio si existen
    if (filters.precioMin) {
        queryParams.set('precio_min', filters.precioMin.toString());
    }

    if (filters.precioMax) {
        queryParams.set('precio_max', filters.precioMax.toString());
    }

    // Agregar término de búsqueda como 'filtro' si existe
    if (searchTerm && searchTerm.trim()) {
        const filtroSlug = textToSlug(searchTerm.trim());
        queryParams.set('filtro', filtroSlug);
    }

    // Construir URL final
    const queryString = queryParams.toString();
    const finalUrl = queryString ? `${path}?${queryString}` : path;

    console.log('🔗 generateFriendlyUrl - URL generada:', finalUrl);
    return finalUrl;
};

/**
 * Verifica si se debe generar URL amigable
 * Solo se genera cuando:
 * 1. Se completó la renderización de productos (first=0)
 * 2. No se está en proceso de generación de URL
 * 3. Hay datos de filtros disponibles
 * @param {number} first - Valor actual de first
 * @param {boolean} isGeneratingUrl - Estado de generación de URL
 * @param {Object} filterData - Datos de filtros
 * @returns {boolean} - True si se debe generar URL
 */
export const shouldGenerateUrl = (first, isGeneratingUrl, filterData) => {
    const shouldGenerate = (
        first === 0 &&                                    // Primera carga/nueva consulta
        !isGeneratingUrl &&                              // No está generando URL actualmente
        filterData &&                                     // Hay datos de filtros
        filterData.categorias &&                         // Hay categorías disponibles
        Array.isArray(filterData.categorias) &&          // Las categorías son un array
        filterData.categorias.length > 0                 // Hay al menos una categoría
    );
    
    console.log('🔗 shouldGenerateUrl - Evaluación:', {
        first,
        isGeneratingUrl,
        hasFilterData: !!filterData,
        hasCategorias: !!(filterData?.categorias?.length),
        shouldGenerate
    });
    
    return shouldGenerate;
};

/**
 * Ejemplos de URLs generadas:
 * 
 * /catalogo/laptops/gaming?marca=asus&canal=americanos
 * /catalogo/laptops?marca=hp&canal=peruanos  
 * /catalogo?marca=lenovo&linea=ideapad&canal=importados
 * /catalogo/accesorios/mouse?marca=logitech
 * /catalogo/componentes?concepto=alta-gama&precio_min=500
 */